import { useConfig } from "$global-providers/config-provider/provider";
import React, { useCallback, useContext, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useLazyUser } from "../user-provider";
import { loadSegmentSnippet } from "./segment";

interface SegmentAnalytics {
  /**
   * https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/#page
   */
  page: (
    name: string,
    properties?: Record<string, string | number | boolean>,
    options?: Record<
      string,
      string | number | boolean | Record<string, string | number | boolean>
    >,
  ) => void;
  /**
   * https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/%23page/#identify
   */
  identify: (
    userId?: string,
    traits?: object,
    options?: Record<
      string,
      string | number | boolean | Record<string, string | number | boolean>
    >,
    callback?: () => void,
  ) => void;
  /**
   * https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/%23page/#track
   */
  track: (
    event: string,
    properties?: Record<string, string | number | boolean>,
    options?: Record<
      string,
      string | number | boolean | Record<string, string | number | boolean>
    >,
    callback?: () => void,
  ) => void;
  /**
   * https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/%23page/#reset-or-log-out
   */
  reset: () => void;
  /**
   * https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/quickstart/#step-2a-add-the-segment-snippet
   *
   * TODO: Possibly want to replace usage of this with a ready listener
   * https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/#ready
   */
  initialize: Array<unknown>;
}

const analyticsOptionsWithoutPagePii = { page: { url: null, search: null } }; // https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/troubleshooting/#can-i-overwrite-the-context-fields

export type EventName =
  // Legacy event names not following our naming convention
  | "networkStatusChange"
  | "appOutOfDateBannerUpdateClicked"
  | "mediaDevices.facingMode.notSupported"
  | "mediaDevices.identifyCameras.bothRejected"
  | "healthPay.dispensePrescription.failure"
  | "healthPay.dispensePrescription.submit"
  | "healthPay.submission.success"
  | "rxdb.pullReplicationInitiated"
  | "rxdb.conflictingInvestigationFound"
  | "patientDataSharingConsentModal.failedToFindPatientInRxDBWhenUpdatingConsent"
  | "rxdb.dailyNumberOfPatientsInRxDB"
  // All new events defined should follow our naming convention
  // See here: https://elephant.slab.com/posts/rfc-analytics-event-naming-convention-swiinli6
  | "Consultation Submitted"
  | "Consultation ReviewInitiated"
  | "Consultation ReturnedToEdit"
  | "Diagnosis Added"
  | "Diagnosis Removed"
  | "Investigation Added"
  | "Investigation Removed"
  | "Investigation OutcomeSelected"
  | "Investigation TestResultImageCaptured"
  | "Investigation RecommendationShown"
  | "Investigation RecommendationSelected"
  | "Symptom RecommendationSelected"
  | "Prescription DrugSelected"
  | "Prescription Removed"
  | "Prescription RecommendationSelected"
  | "Prescription RecommendationShown"
  | "Dispensation Submitted"
  | "Dispensation MedicationSelected"
  | "Dispensation AlternativeMedicationSelected"
  | "Patient RegistrationStepStarted"
  | "Vaccine Added"
  | "Vaccination Completed"
  | "Vaccination NudgeShown"
  | "Vaccination Started"
  // Temporary for an experiment.
  | "PatientRegistrationPayment PartiallyPaid"
  | "NudgeSurvey Submitted";

type Context = {
  trackEvent: (
    event: EventName,
    properties?: Record<
      string,
      string | number | boolean | Record<string, string | number | boolean>
    >,
  ) => void;
};

export const AnalyticsContext = React.createContext<Context>({
  trackEvent: () =>
    NODE_ENV !== "test" &&
    console.warn("Attempted to track event before analytics was initialised"),
});

export interface WindowWithAnalytics extends Window {
  analytics: SegmentAnalytics;
}

export const AnalyticsProvider: FCwC = ({ children }) => {
  const {
    segment: { key: segmentKey },
    environment,
  } = useConfig();
  const user = useLazyUser();
  const pageName = useLocation().pathname;
  const hasInitialised = useRef(false);
  const pageNameReference = useRef(null);
  const userIdReference = useRef(null);

  useEffect(() => {
    if (!hasInitialised.current && segmentKey && environment === "production") {
      loadSegmentSnippet(segmentKey);
      hasInitialised.current = true;
    }
  }, [segmentKey, environment]);

  const windowWithAnalytics = window as unknown as WindowWithAnalytics;
  const analytics = windowWithAnalytics.analytics;

  useEffect(() => {
    if (analytics?.initialize && pageNameReference.current !== pageName) {
      analytics?.page(
        pageName,
        { url: null, search: null },
        analyticsOptionsWithoutPagePii,
      );
      pageNameReference.current = pageName;
    }
  }, [pageName, analytics?.initialize, analytics?.page]);

  useEffect(() => {
    if (analytics?.initialize && userIdReference.current !== user?.id) {
      if (user) {
        analytics?.identify(
          user.id,
          {
            "app.version.semantic": APP_VERSION_SEMANTIC,
            "app.version.commitHash": APP_VERSION_COMMIT_HASH,
            "app.version.date": APP_VERSION_DATE,
          },
          analyticsOptionsWithoutPagePii,
        );
      } else {
        analytics?.reset();
      }
      userIdReference.current = user?.id;
    }
  }, [
    user,
    user?.id,
    analytics?.initialize,
    analytics?.identify,
    analytics?.reset,
  ]);

  const trackEvent = useCallback(
    (event: string, properties?: Record<string, string | number | boolean>) =>
      analytics?.track(event, properties, analyticsOptionsWithoutPagePii),
    [analytics?.track],
  );

  return (
    <AnalyticsContext.Provider value={{ trackEvent }}>
      {children}
    </AnalyticsContext.Provider>
  );
};

export const useAnalytics = (): Context => {
  const context = useContext(AnalyticsContext);
  if (!context) {
    console.debug(
      "Using analytics context outside of provider - returning dummy functions.",
    );
    return { trackEvent: () => {} };
  }
  return context;
};
